import React, { useState, useEffect, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import { JobCard, Allocation, Invoice } from '../../config';
import {
  ProjectFee,
  ProjectFeeSection,
  DISCIPLINES,
  Discipline,
  disciplineLabel,
} from '../../../InvoiceRegister/config';
import { formatCurrency, formatCurrencyCompact } from '../../../../utils/currency';
import PanelHeading from '../../../../components/PanelHeading';
import StatusPill from '../../../../components/StatusPill';
import Skeleton from '../../../../components/Skeleton';
import { resolveProbableFee, TERMINAL_STATUSES } from '../../../Budget/config';
import { aggregateByEmployee, initialsOf } from './aggregate';
import { formatDbDate } from './dates';
import { JobCardModalTab } from './restore';
import './overview-dashboard.scss';

interface OverviewDashboardProps {
  jobCard: JobCard;
  projectId: number;
  stageId: number | null;
  /** The modal is still hydrating `jobCard` from job_cards_list_view. */
  hydrating?: boolean;
  onOpenTab?: (tab: JobCardModalTab) => void;
}

type HealthLevel = 'none' | 'healthy' | 'tracking' | 'eroding' | 'risk' | 'over';

interface MarginStatus {
  level: HealthLevel;
  label: string;
  description: string;
  // Billing has fallen materially behind the effort logged (review trigger).
  reviewFlag: boolean;
  // Right-side figure. Hidden for the `none` states.
  showMetric: boolean;
  metricLabel: string;       // "Projected margin" | "Final margin"
  metricPct: number;         // (fee − cost)/fee × 100, signed: +super profit, −below full margin
  metricAmountText: string;  // e.g. "$20,000 super profit" | "$28,000 profit" | "$5,000 loss"
}

// The dollar bottom line for the right-side figure, from the cost ratio `r`
// (= cost/fee realised, or cost/invoiced projected) and the fee:
//  - r < 1   → super profit = fee × (1 − r)  (coming in under the fee)
//  - r > 2   → real loss = real cost (≈ half) − fee  (actual money lost)
//  - between → still profitable: real profit = fee × (1 − r/2)
const formatMarginAmount = (fee: number, r: number): string => {
  const superProfit = fee * (1 - r);
  if (superProfit >= 0) return `${formatCurrency(superProfit)} super profit`;
  const realProfit = fee * (1 - r / 2);
  if (realProfit >= 0) return `${formatCurrency(realProfit)} profit`;
  return `${formatCurrency(-realProfit)} loss`;
};

// Margin is built into the charge-out rate, so `cost` (hours × charge rate) is
// the charged value of effort, and the Fee is the benchmark:
//   cost = fee → full margin; cost < fee → super profit (fee − cost);
//   cost = 2×fee → break-even (real cost ≈ half); cost > 2×fee → loss.
// While live we PROJECT to the full fee using the current burn rate
// `cost / invoiced` (cost per $ billed), so we don't assume the unbilled scope
// is delivered for free. Once fully invoiced the realised ratio is `cost / fee`.
//
// Billing is ~monthly, so time cost legitimately runs ahead of invoiced between
// claims. We grow a lag allowance with weeks since the last claim (none right
// after invoicing, up to ~+0.4 by the time a monthly claim is due) and divide
// the live ratio by it — so r ≈ 1.2 reads as a concern the day after invoicing
// but as fine four weeks in when a claim is simply due.
const marginStatus = (
  cost: number, invoiced: number, fee: number | null, hours: number, daysSinceInvoice: number | null,
): MarginStatus => {
  const blank = { reviewFlag: false, showMetric: false, metricLabel: '', metricPct: 0, metricAmountText: '' };

  if (fee == null || fee <= 0) {
    return { level: 'none', label: 'No fee on file', description: 'Add a fee plan to track margin against effort.', ...blank };
  }
  if (cost <= 0 && invoiced <= 0) {
    return { level: 'none', label: 'Not started', description: 'No time or invoices logged yet.', ...blank };
  }

  // Nothing billed yet → no basis to project a margin. Flag if a lot of
  // effort has gone in with nothing to show for it.
  if (invoiced <= 0) {
    const reviewFlag = cost > 0.25 * fee;
    return {
      level: 'none', label: 'No invoices yet',
      description: `${hours.toFixed(1)} hrs of effort logged — send the first claim to start tracking.`,
      reviewFlag, showMetric: false, metricLabel: '', metricPct: 0, metricAmountText: '',
    };
  }

  const complete = invoiced >= fee;
  // Lag allowance: 0 right after invoicing, +0.1 per week up to +0.4 (~4 weeks).
  const lagWeeks = Math.min(Math.max(daysSinceInvoice ?? 0, 0) / 7, 4);
  const lagFactor = complete ? 1 : 1 + 0.1 * lagWeeks;
  const r = (complete ? cost / fee : cost / invoiced) / lagFactor;
  const marginPct = (1 - r) * 100; // +super profit %, −% below full margin (of fee)
  const reviewFlag = !complete && r >= 1.5; // still well ahead of billing after the lag allowance

  const metric = {
    reviewFlag, showMetric: true,
    metricLabel: complete ? 'Final margin' : 'Projected margin',
    metricPct: marginPct, metricAmountText: formatMarginAmount(fee, r),
  };

  if (complete) {
    if (r < 1.0) return { level: 'healthy', label: 'Super profit', description: `Delivered under fee — ${Math.round(marginPct)}% super profit realised.`, ...metric };
    if (r <= 1.05) return { level: 'tracking', label: 'Full margin', description: 'Came in on the fee — full built-in margin realised.', ...metric };
    if (r <= 2.0) return { level: 'eroding', label: 'Below full margin', description: `Effort ran over the fee — ${Math.round(-marginPct)}% of the margin given back.`, ...metric };
    return { level: 'over', label: 'Completed at a loss', description: 'Effort exceeded twice the fee — finished below break-even.', ...metric };
  }

  // Live — projected from the current burn rate, after the billing-lag allowance.
  if (r <= 1.0) return { level: 'healthy', label: 'On track', description: 'Time cost is in line with billing for this point in the cycle — projecting full margin.', ...metric };
  if (r <= 1.15) return { level: 'tracking', label: 'Tracking', description: 'Time cost is a touch ahead of billing — projecting around full margin.', ...metric };
  if (r <= 1.5) return { level: 'eroding', label: 'Margin eroding', description: 'Time cost is outpacing billing beyond the usual lag — projected margin below full.', ...metric };
  if (r <= 2.0) return { level: 'risk', label: 'Margin at risk', description: 'Time cost is well ahead of billing — projecting near break-even.', ...metric };
  return { level: 'over', label: 'Running at a loss', description: 'Time cost is far ahead of billing — projecting a loss.', ...metric };
};

// Render the signed margin-vs-fee percentage as an at-a-glance phrase.
const formatMarginPct = (pct: number): string => {
  if (pct >= 0) return `+${Math.round(pct)}% super profit`;
  if (pct > -100) return `${Math.round(-pct)}% below full margin`;
  return 'Loss';
};

type PillTone = React.ComponentProps<typeof StatusPill>['tone'];

// 'eroding' and 'risk' used to share one level, so "Margin eroding" and
// "Margin at risk" — the second projecting near break-even — were painted
// identically. They are different calls to action.
const HEALTH_TONE: Record<HealthLevel, PillTone> = {
  none: 'neutral',
  healthy: 'success',
  tracking: 'info',
  eroding: 'warning',
  risk: 'danger',
  over: 'danger',
};

// recharts takes colours and sizes as props, not classes, so they can't come
// from SCSS. One table mirroring sass/_variables.scss rather than hex scattered
// through the JSX: $success-color, $danger-color, $color-gray-500/-200.
const CHART_TOKENS = {
  invoiced: '#28a745',
  cost: '#dc3545',
  axis: '#6b7280',
  grid: '#e5e7eb',
  tickSize: 11,
} as const;

interface SeriesPoint {
  /** ISO yyyy-mm-dd for x-axis ticks */
  date: string;
  /** Numeric epoch ms — used as the x value so the chart spaces points
   * correctly across time even with sparse data */
  ts: number;
  cost?: number;
  invoiced?: number;
}

/** Merge cumulative cost + cumulative invoiced into a single sorted series so
 * recharts can render both lines off the same x scale. */
const buildSeries = (allocations: Allocation[], invoices: Invoice[]): SeriesPoint[] => {
  // Use recorded_date so the invoiced line tracks the period each invoice is
  // attributed to (matching the Budget page), not the printed invoice date.
  const events: Array<{ date: string; deltaCost?: number; deltaInvoiced?: number }> = [];
  for (const a of allocations) {
    events.push({ date: a.date, deltaCost: a.total_value });
  }
  for (const inv of invoices) {
    events.push({ date: inv.recorded_date ?? inv.invoice_date, deltaInvoiced: inv.our_fee });
  }
  events.sort((a, b) => a.date.localeCompare(b.date));

  let runningCost = 0;
  let runningInvoiced = 0;
  const byDate = new Map<string, SeriesPoint>();
  for (const e of events) {
    if (e.deltaCost != null) runningCost += e.deltaCost;
    if (e.deltaInvoiced != null) runningInvoiced += e.deltaInvoiced;
    byDate.set(e.date, {
      date: e.date,
      ts: parseISO(e.date).getTime(),
      cost: runningCost,
      invoiced: runningInvoiced,
    });
  }
  return Array.from(byDate.values()).sort((a, b) => a.ts - b.ts);
};

const formatCurrencyShort = (n: number): string => {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};

interface TooltipPayloadItem {
  name: string;
  value: number;
  dataKey: string;
}

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: number;
}> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="overview-dashboard__chart-tooltip">
      <div className="overview-dashboard__chart-tooltip-date">
        {label ? formatDbDate(new Date(label).toISOString()) : ''}
      </div>
      {payload.map((entry) => (
        <div key={entry.name} className="overview-dashboard__chart-tooltip-row">
          <span
            className={`overview-dashboard__chart-tooltip-swatch is-${entry.dataKey}`}
          />
          <span className="overview-dashboard__chart-tooltip-label">{entry.name}</span>
          <span className="overview-dashboard__chart-tooltip-value">
            {formatCurrency(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

type BarTone = 'neutral' | 'success' | 'warn' | 'danger';

/** A thin bar. `pct` may exceed 100 — the fill clamps, the tone says "over". */
const Bar: React.FC<{ pct: number; tone: BarTone; hatched?: boolean; label: string }> = ({
  pct,
  tone,
  hatched = false,
  label,
}) => (
  <div
    className={`overview-dashboard__bar${hatched ? ' is-hatched' : ''}`}
    role="img"
    aria-label={label}
  >
    {!hatched && (
      <div
        className={`overview-dashboard__bar-fill is-${tone}`}
        // The one dynamic value in the component: a width can't be a class.
        style={{ width: `${Math.max(0, Math.min(pct, 100))}%` }}
      />
    )}
  </div>
);

interface PhaseRow {
  key: string;
  title: string;
  discipline: Discipline;
  isProbable: boolean;
  isItemBased: boolean;
  quoted: number;
  invoiced: number;
  pct: number;
  qtyInvoiced: number | null;
  qtyTotal: number | null;
}

const toPhaseRow = (sec: ProjectFeeSection, index: number): PhaseRow => {
  const quoted = Number(sec.fee_total) || 0;
  const invoiced = Number(sec.invoiced_to_date) || 0;
  return {
    key: String(sec.id ?? `new-${index}`),
    title: sec.name?.trim() || sec.phase,
    discipline: sec.discipline === 'civil' ? 'civil' : 'structural',
    isProbable: !!sec.is_probable,
    isItemBased: !!sec.is_item_based,
    quoted,
    invoiced,
    pct: quoted > 0 ? (invoiced / quoted) * 100 : 0,
    qtyInvoiced: sec.invoiced_qty ?? null,
    qtyTotal: sec.qty_total ?? null,
  };
};

const phaseTone = (pct: number): BarTone => {
  // Over 100% means the fee was amended below what had already been claimed.
  if (pct > 100.5) return 'danger';
  if (pct >= 99.5) return 'success';
  return 'neutral';
};

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? '' : 's'}`;

const OverviewDashboard: React.FC<OverviewDashboardProps> = ({
  jobCard,
  projectId,
  stageId,
  hydrating = false,
  onOpenTab,
}) => {
  const supabase = useSupabaseClient();
  const [fee, setFee] = useState<ProjectFee | null>(null);
  // Starts true: until the RPC answers we do not know whether there is a fee,
  // and rendering the "No fee on file" state in the meantime told every job
  // with a fee that it had none.
  const [feeLoading, setFeeLoading] = useState<boolean>(true);
  const [feeError, setFeeError] = useState<boolean>(false);
  const [reloadKey, setReloadKey] = useState(0);

  // The active fee (latest non-superseded) with its sections — the fee total,
  // the probable split and each phase's invoiced-to-date all come from here.
  useEffect(() => {
    let cancelled = false;
    setFeeLoading(true);
    setFeeError(false);
    (async () => {
      const { data, error } = await supabase.rpc('get_project_fee_for_entity', {
        p_project_id: projectId,
        p_stage_id: stageId,
      });
      if (cancelled) return;
      if (error) {
        console.error('OverviewDashboard: failed to load fee', error);
        setFee(null);
        setFeeError(true);
      } else {
        setFee((data as ProjectFee | null) ?? null);
      }
      setFeeLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, projectId, stageId, reloadKey]);

  const loading = feeLoading || hydrating;
  // A fee record with nothing priced yet (the register's "Awaiting Fee") is
  // no fee for this tab's purposes. Left as 0 it produced "Won fee $0" beside
  // "Won fee remaining -$2,000 · No fee on file".
  const feeTotal = fee && Number(fee.total_ex_gst) > 0 ? Number(fee.total_ex_gst) : null;
  // Everything on this tab is measured against WON fee: the quoted fee less
  // the phases flagged probable. Probable scope is quoted but not awarded, so
  // it can't be invoiced — counting it would make every job with a probable
  // phase look further from fully billed (and better margined) than it is.
  // The split is the Budget page's, status rule included (resolveProbableFee):
  // a job still AT status Probable is wholly probable, a finished one has
  // none — so this tile and the Budget row the modal was opened from agree.
  const probableTotal = useMemo(() => {
    const flagged = (fee?.sections ?? [])
      .filter((sec) => sec.is_probable)
      .reduce((sum, sec) => sum + (Number(sec.fee_total) || 0), 0);
    return resolveProbableFee(jobCard.workflow_status, feeTotal ?? 0, flagged);
  }, [fee, feeTotal, jobCard.workflow_status]);
  const wonFee = feeTotal != null ? Math.max(feeTotal - probableTotal, 0) : null;
  // A fee exists but none of it has been awarded yet.
  const whollyProbable = feeTotal != null && feeTotal > 0 && wonFee === 0;

  const allocations = useMemo<Allocation[]>(() => jobCard.allocations ?? [], [jobCard.allocations]);
  const invoices = useMemo<Invoice[]>(() => jobCard.invoices ?? [], [jobCard.invoices]);
  const totalCost = jobCard.total_cost ?? 0;
  const totalInvoiced = jobCard.total_invoiced ?? 0;
  const totalHours = jobCard.total_hours ?? 0;

  // Most recent actual claim (Sent/Entered) — drives the billing-lag
  // allowance in the margin projection, and the Last invoice tile.
  const lastInvoiceDate = useMemo<string | null>(() => {
    const dates = invoices
      .filter((i) => i.status === 'Sent' || i.status === 'Entered')
      .map((i) => i.invoice_date)
      .filter(Boolean);
    if (dates.length === 0) return null;
    return dates.reduce((a, b) => (a > b ? a : b));
  }, [invoices]);
  const daysSinceInvoice = lastInvoiceDate
    ? Math.max(0, differenceInCalendarDays(new Date(), parseISO(lastInvoiceDate)))
    : null;
  const draftCount = invoices.filter((i) => i.status === 'Draft').length;

  const health = useMemo<MarginStatus>(() => {
    if (whollyProbable) {
      // marginStatus would call a zero won fee "No fee on file", which is
      // wrong: there is a fee, it just hasn't been won.
      return {
        level: 'none', label: 'Not yet won',
        description: 'The whole fee is still probable — margin is tracked once scope is awarded.',
        reviewFlag: false, showMetric: false, metricLabel: '', metricPct: 0, metricAmountText: '',
      };
    }
    return marginStatus(totalCost, totalInvoiced, wonFee, totalHours, daysSinceInvoice);
  }, [whollyProbable, totalCost, totalInvoiced, wonFee, totalHours, daysSinceInvoice]);

  const hasFee = wonFee != null && wonFee > 0;
  const feeRemaining = wonFee != null && !whollyProbable ? wonFee - totalInvoiced : null;
  const invoicedOfFeePct = hasFee ? (totalInvoiced / (wonFee as number)) * 100 : 0;
  // One figure for both the bar and its label, unclamped: the old bar capped
  // at 150% while the label beside it did its own division, so past that
  // point the two disagreed.
  const costOfInvoicedPct = totalInvoiced > 0 ? (totalCost / totalInvoiced) * 100 : 0;
  const costTone: BarTone =
    costOfInvoicedPct > 100 ? 'danger' : costOfInvoicedPct > 90 ? 'warn' : 'neutral';

  // Fee phases. Probable phases are listed (the scope exists) but kept out of
  // the headline %, which is measured against WON fee — the same rule the
  // Budget page applies to Won Fee and Unallocated.
  const phaseRows = useMemo<PhaseRow[]>(
    () => {
      // The status rule reaches the rows too, or the list would contradict
      // the tile above it: every phase of a job still at Probable is
      // unawarded, and none on a finished job is.
      const status = jobCard.workflow_status ?? '';
      const forced = status === 'Probable' ? true : TERMINAL_STATUSES.includes(status) ? false : null;
      return (fee?.sections ?? [])
        .map(toPhaseRow)
        .map((r) => (forced == null ? r : { ...r, isProbable: forced }))
        .filter((r) => r.quoted > 0 || r.invoiced > 0);
    },
    [fee, jobCard.workflow_status],
  );
  const phaseGroups = useMemo(
    () =>
      DISCIPLINES.map((d) => ({
        discipline: d,
        rows: phaseRows.filter((r) => r.discipline === d),
      })).filter((g) => g.rows.length > 0),
    [phaseRows],
  );
  const wonRows = phaseRows.filter((r) => !r.isProbable);
  const wonQuoted = wonRows.reduce((s, r) => s + r.quoted, 0);
  const wonInvoiced = wonRows.reduce((s, r) => s + r.invoiced, 0);
  const wonPct = wonQuoted > 0 ? (wonInvoiced / wonQuoted) * 100 : 0;

  const series = useMemo(() => buildSeries(allocations, invoices), [allocations, invoices]);
  const employeeAggregates = useMemo(
    () => aggregateByEmployee(allocations).sort((a, b) => b.total_value - a.total_value),
    [allocations],
  );

  if (loading) {
    return (
      <div className="overview-dashboard" aria-busy="true">
        <Skeleton variant="rectangular" height={72} borderRadius={10} />
        <div className="overview-dashboard__stats">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="overview-dashboard__stat">
              <Skeleton width="60%" height={11} />
              <Skeleton width="80%" height={20} />
              <Skeleton width="50%" height={11} />
            </div>
          ))}
        </div>
        <Skeleton variant="rectangular" height={160} borderRadius={10} />
        <Skeleton variant="rectangular" height={280} borderRadius={8} />
      </div>
    );
  }

  return (
    <div className="overview-dashboard">
      {feeError && (
        <div className="overview-dashboard__error" role="alert">
          <span>The fee for this job could not be loaded, so the figures below are incomplete.</span>
          <button
            type="button"
            className="pill-btn pill-btn--sm"
            onClick={() => setReloadKey((k) => k + 1)}
          >
            Try Again
          </button>
        </div>
      )}

      {/* 1. Health */}
      <section className={`overview-dashboard__health is-${health.level}`}>
        <div className="overview-dashboard__health-main">
          <div className="overview-dashboard__health-pills">
            <StatusPill label={health.label} tone={HEALTH_TONE[health.level]} />
            {health.reviewFlag && (
              <StatusPill label="Billing behind effort — review" tone="warning" />
            )}
          </div>
          <p className="overview-dashboard__health-description">{health.description}</p>
        </div>
        {health.showMetric && (
          <div className="overview-dashboard__health-metric">
            <span className="overview-dashboard__health-metric-label">{health.metricLabel}</span>
            <span className="overview-dashboard__health-metric-value">
              {formatMarginPct(health.metricPct)}
            </span>
            <span className="overview-dashboard__health-metric-sub">{health.metricAmountText}</span>
          </div>
        )}
      </section>

      {/* 2. Headline figures */}
      <div className="overview-dashboard__stats">
        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Won fee</span>
          <span className="overview-dashboard__stat-value">
            {wonFee == null || whollyProbable ? '—' : formatCurrencyCompact(wonFee)}
          </span>
          <span
            className="overview-dashboard__stat-sub"
            title={
              feeTotal != null && probableTotal > 0
                ? `${formatCurrency(feeTotal)} quoted in total, ex GST`
                : undefined
            }
          >
            {feeTotal == null
              ? 'No fee on file'
              : probableTotal > 0
                ? `+ ${formatCurrencyCompact(probableTotal)} probable`
                : 'Ex GST'}
          </span>
        </div>

        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Invoiced to date</span>
          <span className="overview-dashboard__stat-value">{formatCurrencyCompact(totalInvoiced)}</span>
          {hasFee && (
            <Bar
              pct={invoicedOfFeePct}
              tone={invoicedOfFeePct > 100.5 ? 'danger' : 'neutral'}
              label={`${Math.round(invoicedOfFeePct)}% of won fee invoiced`}
            />
          )}
          <span className="overview-dashboard__stat-sub">
            {hasFee
              ? `${Math.round(invoicedOfFeePct)}% of won fee`
              : plural(jobCard.invoice_count ?? 0, 'invoice')}
          </span>
        </div>

        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Time cost</span>
          <span className="overview-dashboard__stat-value">{formatCurrencyCompact(totalCost)}</span>
          {totalInvoiced > 0 && (
            <Bar
              pct={costOfInvoicedPct}
              tone={costTone}
              label={`Time cost is ${Math.round(costOfInvoicedPct)}% of invoiced`}
            />
          )}
          <span className={`overview-dashboard__stat-sub${costTone === 'danger' ? ' is-danger' : ''}`}>
            {totalInvoiced > 0 ? `${Math.round(costOfInvoicedPct)}% of invoiced` : 'Nothing invoiced yet'}
          </span>
        </div>

        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Won fee remaining</span>
          <span
            className={`overview-dashboard__stat-value${
              feeRemaining != null && feeRemaining < 0 ? ' is-danger' : ''
            }`}
          >
            {feeRemaining != null ? formatCurrencyCompact(feeRemaining) : '—'}
          </span>
          <span className="overview-dashboard__stat-sub">
            {whollyProbable
              ? 'Nothing won yet'
              : !hasFee
              ? 'No fee on file'
              : feeRemaining != null && feeRemaining < 0
                ? 'Invoiced over the fee'
                : `${Math.round(Math.max(100 - invoicedOfFeePct, 0))}% un-invoiced`}
          </span>
        </div>

        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Hours</span>
          <span className="overview-dashboard__stat-value">{totalHours.toFixed(1)}</span>
          <span className="overview-dashboard__stat-sub">
            {employeeAggregates.length === 1 ? '1 person' : `${employeeAggregates.length} people`}
          </span>
        </div>

        <div className="overview-dashboard__stat">
          <span className="overview-dashboard__stat-label">Last invoice</span>
          <span className="overview-dashboard__stat-value is-text">
            {lastInvoiceDate ? formatDbDate(lastInvoiceDate, 'd MMM yy') : '—'}
          </span>
          <span className="overview-dashboard__stat-sub">
            {daysSinceInvoice == null
              ? 'Nothing sent yet'
              : daysSinceInvoice === 0
                ? 'Today'
                : `${plural(daysSinceInvoice, 'day')} ago`}
            {draftCount > 0 ? ` · ${plural(draftCount, 'draft')}` : ''}
          </span>
        </div>
      </div>

      {/* 3. Fee phases — how much of each has been invoiced */}
      <section className="overview-dashboard__section">
        <PanelHeading
          title="Fee phases"
          spaced="sm"
          meta={
            wonQuoted > 0
              ? `${Math.round(wonPct)}% of won fee invoiced`
              : undefined
          }
          action={
            onOpenTab ? (
              <button
                type="button"
                className="panel-heading__action"
                onClick={() => onOpenTab('fees')}
              >
                Open fees
              </button>
            ) : undefined
          }
        />
        {phaseRows.length === 0 ? (
          <div className="ui-table__empty">
            {feeError
              ? 'Fee phases are unavailable.'
              : 'No fee on file — add one on the Fees tab to track invoicing by phase.'}
          </div>
        ) : (
          <div className="overview-dashboard__phases">
            {phaseGroups.map((group) => (
              <React.Fragment key={group.discipline}>
                {phaseGroups.length > 1 && (
                  <div className="overview-dashboard__phase-group">
                    {disciplineLabel(group.discipline)}
                  </div>
                )}
                {group.rows.map((row) => (
                  <div
                    key={row.key}
                    className={`overview-dashboard__phase${row.isProbable ? ' is-probable' : ''}`}
                  >
                    <div className="overview-dashboard__phase-name">
                      <span className="overview-dashboard__phase-title" title={row.title}>
                        {row.title}
                      </span>
                      {row.isProbable && <span className="ui-chip">Probable</span>}
                    </div>
                    <Bar
                      pct={row.pct}
                      tone={phaseTone(row.pct)}
                      hatched={row.isProbable}
                      label={
                        row.isProbable
                          ? `${row.title}: not awarded`
                          : `${row.title}: ${Math.round(row.pct)}% invoiced`
                      }
                    />
                    <span
                      className={`overview-dashboard__phase-pct is-${
                        row.isProbable ? 'muted' : phaseTone(row.pct)
                      }`}
                    >
                      {row.isProbable
                        ? 'Not awarded'
                        : row.isItemBased && row.qtyTotal
                          ? `${row.qtyInvoiced ?? 0} of ${row.qtyTotal}`
                          : `${Math.round(row.pct)}%`}
                    </span>
                    <span className="overview-dashboard__phase-amount">
                      <strong>{formatCurrencyCompact(row.invoiced)}</strong>
                      {' of '}
                      {formatCurrencyCompact(row.quoted)}
                    </span>
                    <span className="overview-dashboard__phase-remaining">
                      {row.isProbable
                        ? '—'
                        : row.quoted - row.invoiced < 0
                          ? `${formatCurrencyCompact(row.invoiced - row.quoted)} over`
                          : `${formatCurrencyCompact(row.quoted - row.invoiced)} left`}
                    </span>
                  </div>
                ))}
              </React.Fragment>
            ))}
            {wonRows.length > 1 && (
              <div className="overview-dashboard__phase is-total">
                <div className="overview-dashboard__phase-name">
                  <span className="overview-dashboard__phase-title">
                    Won fee{probableTotal > 0 ? ' (excl. probable)' : ''}
                  </span>
                </div>
                <Bar
                  pct={wonPct}
                  tone={phaseTone(wonPct)}
                  label={`${Math.round(wonPct)}% of won fee invoiced`}
                />
                <span className={`overview-dashboard__phase-pct is-${phaseTone(wonPct)}`}>
                  {Math.round(wonPct)}%
                </span>
                <span className="overview-dashboard__phase-amount">
                  <strong>{formatCurrencyCompact(wonInvoiced)}</strong>
                  {' of '}
                  {formatCurrencyCompact(wonQuoted)}
                </span>
                <span className="overview-dashboard__phase-remaining">
                  {wonQuoted - wonInvoiced < 0
                    ? `${formatCurrencyCompact(wonInvoiced - wonQuoted)} over`
                    : `${formatCurrencyCompact(wonQuoted - wonInvoiced)} left`}
                </span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* 4. Cumulative chart */}
      <section className="overview-dashboard__section">
        <PanelHeading
          title="Cost vs invoiced over time"
          spaced="sm"
          meta="Cumulative · dashed line is the won fee"
        />
        {series.length === 0 ? (
          <div className="ui-table__empty">No allocations or invoices to chart yet.</div>
        ) : (
          <div className="overview-dashboard__chart">
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_TOKENS.grid} vertical={false} />
                <XAxis
                  dataKey="ts"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  scale="time"
                  tickFormatter={(ts: number) => formatDbDate(new Date(ts).toISOString(), 'MMM yy')}
                  stroke={CHART_TOKENS.axis}
                  fontSize={CHART_TOKENS.tickSize}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={formatCurrencyShort}
                  stroke={CHART_TOKENS.axis}
                  fontSize={CHART_TOKENS.tickSize}
                  tickLine={false}
                  axisLine={false}
                  width={48}
                />
                <Tooltip content={<ChartTooltip />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  height={24}
                  iconType="plainline"
                  wrapperStyle={{ fontSize: CHART_TOKENS.tickSize }}
                />
                <Line
                  type="monotone"
                  dataKey="invoiced"
                  name="Invoiced"
                  stroke={CHART_TOKENS.invoiced}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="cost"
                  name="Time cost"
                  stroke={CHART_TOKENS.cost}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                {hasFee && (
                  <ReferenceLine
                    y={wonFee as number}
                    stroke={CHART_TOKENS.axis}
                    strokeDasharray="6 4"
                    label={{
                      value: `Won fee ${formatCurrencyShort(wonFee as number)}`,
                      position: 'insideTopRight',
                      fill: CHART_TOKENS.axis,
                      fontSize: CHART_TOKENS.tickSize,
                    }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      {/* 5. Team */}
      <section className="overview-dashboard__section">
        <PanelHeading
          title="Team"
          spaced="sm"
          meta={
            employeeAggregates.length > 0
              ? `${totalHours.toFixed(1)} hrs · ${formatCurrencyCompact(totalCost)}`
              : undefined
          }
          action={
            onOpenTab && employeeAggregates.length > 0 ? (
              <button
                type="button"
                className="panel-heading__action"
                onClick={() => onOpenTab('timesheets')}
              >
                Open time
              </button>
            ) : undefined
          }
        />
        {employeeAggregates.length === 0 ? (
          <div className="ui-table__empty">No time has been allocated to this job yet.</div>
        ) : (
          <div className="overview-dashboard__team">
            {employeeAggregates.map((emp) => {
              const share = totalCost > 0 ? (emp.total_value / totalCost) * 100 : 0;
              return (
                <div key={emp.employee_name} className="overview-dashboard__member">
                  <span className="ui-card__avatar" aria-hidden="true">
                    {initialsOf(emp.employee_name)}
                  </span>
                  <div className="overview-dashboard__member-main">
                    <div className="overview-dashboard__member-top">
                      <span className="overview-dashboard__member-name">{emp.employee_name}</span>
                      {emp.employee_type && <span className="ui-chip">{emp.employee_type}</span>}
                    </div>
                    <Bar
                      pct={share}
                      tone="neutral"
                      label={`${Math.round(share)}% of time cost`}
                    />
                  </div>
                  <span className="overview-dashboard__member-hours">{emp.hours.toFixed(1)} hrs</span>
                  <span className="overview-dashboard__member-amount">
                    {formatCurrencyCompact(emp.total_value)}
                  </span>
                  <span className="overview-dashboard__member-share">{Math.round(share)}%</span>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default OverviewDashboard;
